import { Input } from "@mantine/core";
import { t } from "i18next";
import { useCallback, useMemo } from "react";

import { useCompany } from "#modules/company/view/useCompany";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { getUnitFactorsByMaterialType } from "#modules/quoteRequestLines/domain/getUnitFactors";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { Flex } from "#shared/view/components/Flex";
import { Text } from "#shared/view/components/Text";
import { isValidUnitFactor, UnitName } from "#shared/view/designSystem/inputs/units";
import {
  formatPrice,
  getMaterialDensity,
  MaterialLayout,
  PartTypeSchema,
  PricingModeSchema,
} from "shared";

type MaterialPriceProps = {
  materialGrade: QuoteRequestLineFormData["materialGrade"];
  part: PartDto | undefined;
  materialLayout: MaterialLayout;
};

const MaterialPrice = ({ materialGrade, part, materialLayout }: MaterialPriceProps) => {
  const { company } = useCompany();
  const isVolumetric = materialLayout.pricingMode === PricingModeSchema.enum.VOLUMETRIC;
  const partType = part?.type || PartTypeSchema.enum.SHEET;
  const density = materialLayout.density || getMaterialDensity({ materialGrade });

  const unitFactors = useMemo(() => {
    if (isVolumetric) {
      return undefined;
    }

    return getUnitFactorsByMaterialType({
      partType,
      material: {
        densityKgDm3: density,
        height: materialLayout.height,
        width: materialLayout.width,
        length: materialLayout.length,
        tubeThickness: materialLayout.tubeThickness ?? part?.tubeThickness ?? 0,
      },
    });
  }, [
    density,
    isVolumetric,
    materialLayout.height,
    materialLayout.length,
    materialLayout.tubeThickness,
    materialLayout.width,
    part?.tubeThickness,
    partType,
  ]);

  const getDefaultUnitFactor = useCallback(
    (defaultMaterialPriceUnit: UnitName | undefined) => {
      return unitFactors
        ?.concat({ unit: "€", value: 1 })
        .find((factor) => factor.unit === defaultMaterialPriceUnit);
    },
    [unitFactors],
  );

  const defaultUnitFactor = useMemo(() => {
    if (isVolumetric) {
      return { value: 1, unit: "€/kg" as const };
    }

    return isValidUnitFactor(company?.companySettings.defaultMaterialPriceUnit, unitFactors || [])
      ? getDefaultUnitFactor(company.companySettings.defaultMaterialPriceUnit)
      : { value: 1, unit: "€" as const };
  }, [
    company?.companySettings.defaultMaterialPriceUnit,
    getDefaultUnitFactor,
    isVolumetric,
    unitFactors,
  ]);

  const priceInCents = isVolumetric ? materialLayout.volumetricPrice : materialLayout.price;
  const factor = defaultUnitFactor?.value ?? 1;
  const unit = defaultUnitFactor?.unit ?? "€";

  return (
    <Input.Wrapper>
      <Input.Label>{t("material.price")}</Input.Label>
      <Flex h={36} align="center">
        <Text>
          {formatPrice((priceInCents ?? 0) * factor, unit, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      </Flex>
    </Input.Wrapper>
  );
};

export default MaterialPrice;
